import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import BackButton from '../BackButton';
import ProjectFileComponent from './projectFile';
import "./projectFile.css";

// Enhanced project data structure
const projectsData = [
  {
    id: "compression-of-thought",
    title: "Compression of Thought",
    summary: "Learning to Compress Text using only a Reward Signal",
    tags: ["Reinforcement Learning"],
    status: "Completed",
    date: "2025-6-10",
    links: {
      github: "https://github.com/lanpouthakoun/PromptCompression",
      paper:"https://drive.google.com/file/d/1gG9wvMhvyybdRzAp0_aI9KKsVs7AL-Gz/view?usp=sharing"
    },
    featured: true
  },
  {
    id: "preft",
    title: "PreFT: Prefill-only finetuning for efficient inference",
    summary: "Parameter efficient finetuning technique that is only applied at the prompt level",
    tags: ["Inference"],
    status: "Completed",
    date: "2026-5-7",
    links: {
      github: "https://github.com/stanfordnlp/preft",
      paper:"https://arxiv.org/abs/2605.14217"
    },
    featured: true
  },
  {
    id: "fast-fitting-jaxley",
    title: "Fast Fitting Jaxley",
    summary: "Fitting a Retinal Ganglion Cell using the Jaxley Library",
    tags: ["Machine Learning", "Neuroscience"],
    status: "Completed",
    date: "2025-6-9",
    links: {
      github: "https://github.com/lanpouthakoun/fast-fitting-jaxley",
      paper: "https://drive.google.com/file/d/19Q5lQ48xBAiNI9D14-jnLZDoHNRBAwQa/view?usp=sharing"
    },
    featured: false
  },
  {
    id: "voice-web-agent",
    title: "Voice Web Agent",
    summary: "Voice to Voice Web Browsing Agent",
    tags: ["Machine Learning", "Agent"],
    status: "Completed",
    date: "2026-1-1",
    links: {
      github: "https://github.com/lanpouthakoun/voice_web_agent",
      demo: "https://www.loom.com/share/069e9393c3054117b9d4d769f60539cc"
    },
    featured: false
  },
  {
    id: "deep-alignment",
    title: "POMDP For Deep Alignment",
    summary: "Voice to Voice Web Browsing Agent",
    tags: ["Machine Learning", "Reinforcement Learning", "Alignment"],
    status: "Completed",
    date: "2025-12-15",
    links: {
      paper: "files/deepalignment/DeepAlignment.pdf",
      github: "https://github.com/lanpouthakoun/Deep_Alignment",
    },
    featured: false
  },
  

];

const FilterNav = ({ 
  selectedTags, 
  setSelectedTags, 
  selectedStatus, 
  setSelectedStatus,
  allTags,
  showFeaturedOnly,
  setShowFeaturedOnly 
}) => {
  const toggleTag = (tag) => {
    setSelectedTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const toggleStatus = (status) => {
    setSelectedStatus(prev => prev === status ? '' : status);
  };

  const toggleFeatured = () => {
    setShowFeaturedOnly(prev => !prev);
  };

  return (
    <nav className="filter-nav">
      <div className="filter-group">
        <button
          onClick={toggleFeatured}
          className={`filter-button ${showFeaturedOnly ? 'active' : ''}`}
        >
          Things I'm the Most Proud Of
        </button>
        <button
          onClick={() => {
            setSelectedTags([]);
            setSelectedStatus('');
            setShowFeaturedOnly(false);
          }}
          className="filter-button"
        >
          Everything
        </button>
        <button
          onClick={() => toggleStatus('Completed')}
          className={`filter-button ${selectedStatus === 'Completed' ? 'active' : ''}`}
        >
          Completed
        </button>
        <button
          onClick={() => toggleStatus('In Progress')}
          className={`filter-button ${selectedStatus === 'In Progress' ? 'active' : ''}`}
        >
          In Progress
        </button>
      </div>
      
      <div className="filter-group">
        {allTags.map((tag) => (
          <button
            key={tag}
            onClick={() => toggleTag(tag)}
            className={`filter-button ${selectedTags.includes(tag) ? 'active' : ''}`}
          >
            {tag}
          </button>
        ))}
      </div>
    </nav>
  );
};

const Projects = () => {
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [showFeaturedOnly, setShowFeaturedOnly] = useState(false);
  const navigate = useNavigate();

  // Get all unique tags
  const allTags = useMemo(() => {
    const tags = new Set();
    projectsData.forEach(project => {
      project.tags.forEach(tag => tags.add(tag));
    });
    return Array.from(tags).sort();
  }, []);

  // Filter projects
  const filteredProjects = useMemo(() => {
    return projectsData.filter(project => {
      const matchesTags = selectedTags.length === 0 || 
                         selectedTags.every(tag => project.tags.includes(tag));
      
      const matchesStatus = !selectedStatus || project.status === selectedStatus;
      
      const matchesFeatured = !showFeaturedOnly || project.featured;

      return matchesTags && matchesStatus && matchesFeatured;
    });
  }, [selectedTags, selectedStatus, showFeaturedOnly]);

  const handleProjectClick = (project) => {
    // Navigate to the dynamic project route, passing the project data
    navigate(`/projects/${project.id}`, { state: { project } });
  };

  return (
    
    <div className='projectPage'>
      <BackButton />
      
      <header className="page-header">
        <h1>Projects</h1>
        <p className="page-subtitle">
           For You to See!
        </p>
      </header>

      <FilterNav
        selectedTags={selectedTags}
        setSelectedTags={setSelectedTags}
        selectedStatus={selectedStatus}
        setSelectedStatus={setSelectedStatus}
        allTags={allTags}
        showFeaturedOnly={showFeaturedOnly}
        setShowFeaturedOnly={setShowFeaturedOnly}
      />

      <div className='projectList'>
        {filteredProjects.map((project) => (
          <ProjectFileComponent
            key={project.id}
            id={project.id}
            title={project.title}
            summary={project.summary}
            project={project}
            onClick={() => handleProjectClick(project)}
          />
        ))}
      </div>

      {filteredProjects.length === 0 && (
        <div className="no-results">
          <p>No projects match these filters</p>
        </div>
      )}
    </div>
  );
};

// Export the projects data for use in individual project pages
export { projectsData };
export default Projects;